import { useState, useEffect, useRef } from 'react';
import { pdfjsLib } from '../../lib/pdfWorker';
import type { PDFDocumentProxy } from '../../lib/pdfWorker';
import { describeNonPdfResponse, isPdfArrayBuffer } from '../../lib/pdfBinary';

interface UsePdfDocumentResult {
  document: PDFDocumentProxy | null;
  numPages: number;
  loading: boolean;
  error: string | null;
}

/**
 * Hook: Fetch a PDF and load it via pdf.js.
 * Returns the PDFDocumentProxy for page-level rendering.
 */
export function usePdfDocument(
  url: string | null,
  getToken?: () => string | null,
): UsePdfDocumentResult {
  const [document, setDocument] = useState<PDFDocumentProxy | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const prevUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!url || url === prevUrlRef.current) return;
    prevUrlRef.current = url;

    let cancelled = false;
    let doc: PDFDocumentProxy | null = null;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const headers: Record<string, string> = {};
        const token = getToken?.();
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const response = await fetch(url, { headers });
        if (!response.ok) {
          let message = `Download failed (${response.status}).`;
          try {
            const errBuf = await response.arrayBuffer();
            const preview = new TextDecoder().decode(
              new Uint8Array(errBuf, 0, Math.min(256, errBuf.byteLength)),
            );
            message = describeNonPdfResponse(
              response.status,
              response.headers.get('Content-Type'),
              preview,
            );
          } catch {
            /* keep generic */
          }
          throw new Error(message);
        }
        const data = await response.arrayBuffer();
        if (cancelled) return;

        if (!isPdfArrayBuffer(data)) {
          const previewUtf8 = new TextDecoder().decode(
            new Uint8Array(data, 0, Math.min(256, data.byteLength)),
          );
          throw new Error(
            describeNonPdfResponse(
              response.status,
              response.headers.get('Content-Type'),
              previewUtf8,
            ),
          );
        }

        doc = await pdfjsLib.getDocument({ data }).promise;
        if (cancelled) { doc.destroy(); return; }

        setDocument(doc);
        setNumPages(doc.numPages);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load PDF');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (doc) doc.destroy();
    };
  }, [url, getToken]);

  return { document, numPages, loading, error };
}
